import { useFormatter } from '@client/hooks/utils/useFormatter';
import { cn } from '@client/utils';
import { Avatar, Card, Divider, Popover, Typography } from 'antd';
import { Mail, Phone } from 'lucide-react';
import { type FC, type ReactNode, useState } from 'react';

type CustomItem = {
    type?: 'custom';
    layout?: 'horizontal' | 'vertical';
    icon: FC<{ className?: string }>;
    label: string;
    value: ReactNode;
    copyableProps?: {
        value: string | null | undefined;
    };
};

function ContentItem({ item }: { item: Omit<CustomItem, 'type'> }) {
    return (
        <div className={cn('mb-2 flex items-start gap-2 last:mb-0', item.layout === 'vertical' && 'flex-col')}>
            <div className="flex items-center gap-2">
                <div className="mt-0.5 size-4">{<item.icon className="size-4" />}</div>
                <Typography.Text strong className="whitespace-nowrap text-muted-foreground">
                    {item.label}:
                </Typography.Text>
            </div>
            <div className={cn(item.layout === 'vertical' && 'pl-6')}>
                {item.value ? (
                    item.copyableProps ? (
                        <Typography.Text
                            copyable={
                                item.copyableProps.value
                                    ? {
                                          text: item.copyableProps.value,
                                      }
                                    : undefined
                            }
                        >
                            {item.value}
                        </Typography.Text>
                    ) : (
                        item.value
                    )
                ) : (
                    <Typography.Text>-</Typography.Text>
                )}
            </div>
        </div>
    );
}

export function EntityPopover({
    id,
    canBeOpened,
    isLoading,
    avatar,
    title,
    subtitle,
    items,
    errorContent,
    onOpenIntentChange,
    children,
}: {
    id?: string;
    canBeOpened: boolean;
    isLoading?: boolean;
    avatar: {
        icon?: FC<{ className?: string }>;
        src?: string;
        children?: ReactNode;
    };
    title: string;
    subtitle: ReactNode;
    items: (
        | {
              type: 'email';
              value: string | null | undefined;
          }
        | {
              type: 'phone';
              value: string | null | undefined;
          }
        | CustomItem
    )[];
    errorContent?: ReactNode;
    onOpenIntentChange?: (isOpen: boolean) => void;
    children: ReactNode;
}) {
    const { formatPhoneNumber } = useFormatter();

    const [openIntent, setOpenIntent] = useState(false);
    const isOpen = openIntent && (canBeOpened || isLoading);

    return (
        <Popover
            id={id}
            open={isOpen}
            trigger={['hover', 'focus']}
            placement="right"
            zIndex={2000}
            mouseEnterDelay={0.5}
            onOpenChange={(isOpenParam) => {
                setOpenIntent(isOpenParam);
                onOpenIntentChange?.(isOpenParam);
            }}
            content={
                errorContent ?? (
                    <Card loading={isLoading} variant="outlined" className="min-w-xs max-w-sm shadow-none">
                        <div className="mb-3 flex items-center gap-3">
                            <Avatar
                                size={48}
                                src={avatar.src}
                                className="shrink-0 border border-separator bg-accent-100"
                                icon={avatar.icon && <avatar.icon className="size-5 text-accent-600" />}
                            >
                                {avatar.children}
                            </Avatar>
                            <div className="overflow-hidden">
                                <Typography.Title level={5} className="m-0 overflow-hidden text-ellipsis">
                                    {title}
                                </Typography.Title>
                                <Typography.Text
                                    type="secondary"
                                    className="flex w-full items-center gap-1 overflow-hidden text-ellipsis text-sm"
                                >
                                    {subtitle}
                                </Typography.Text>
                            </div>
                        </div>

                        <Divider className="my-2" />

                        {items.map((item, index) => (
                            <>
                                {item.type === 'email' ? (
                                    <ContentItem
                                        key={index}
                                        item={{
                                            icon({ className, ...props }) {
                                                return <Mail className={cn(className, 'text-blue-500')} {...props} />;
                                            },
                                            label: 'Email',
                                            value: item.value && (
                                                <a
                                                    href={`mailto:${item.value}`}
                                                    className="truncate text-primary hover:underline"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    {item.value}
                                                </a>
                                            ),
                                            copyableProps: {
                                                value: item.value,
                                            },
                                        }}
                                    />
                                ) : item.type === 'phone' ? (
                                    <ContentItem
                                        key={index}
                                        item={{
                                            icon({ className, ...props }) {
                                                return <Phone className={cn(className, 'text-green-500')} {...props} />;
                                            },
                                            label: 'Phone',
                                            value: item.value && (
                                                <a
                                                    href={`tel:${item.value}`}
                                                    className="truncate text-primary hover:underline"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    {formatPhoneNumber(item.value)}
                                                </a>
                                            ),
                                            copyableProps: {
                                                value: item.value,
                                            },
                                        }}
                                    />
                                ) : (
                                    <ContentItem key={index} item={item} />
                                )}
                            </>
                        ))}
                    </Card>
                )
            }
        >
            {children}
        </Popover>
    );
}
